import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FiDownload, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { getAdminTripsCheckClaimsFullList, getAllPartners } from '../../services/api';
import { flattenTripsCheckClaimsForExcel } from '../../utils/tripsCheckClaimsExcelRows';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsClaims.css';

const emptyFilters = () => ({
    policy_code: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
    client_email: '',
    company_name: '',
    status: '',
    activity_start_date_from: '',
    activity_start_date_to: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    trip_modified: '',
    new_trip_start_date_from: '',
    new_trip_start_date_to: '',
    new_trip_end_date_from: '',
    new_trip_end_date_to: '',
    new_trip_effective_start_date_from: '',
    new_trip_effective_start_date_to: '',
    new_trip_effective_end_date_from: '',
    new_trip_effective_end_date_to: '',
    trip_day: '',
});

const buildQueryFromFilters = (f) => {
    const out = {};
    Object.keys(f).forEach((key) => {
        const value = f[key];
        if (key === 'trip_modified') {
            if (value === 'true' || value === 'false') {
                out[key] = value;
            }
            return;
        }
        if (value && String(value).trim() !== '') {
            out[key] = String(value).trim();
        }
    });
    return out;
};

const AdminTripsCheckClaimsExport = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initial = useMemo(
        () => (location.state?.filters && typeof location.state.filters === 'object' ? location.state.filters : {}),
        [location.state]
    );

    const [filters, setFilters] = useState(() => {
        const base = emptyFilters();
        Object.keys(base).forEach((k) => {
            if (initial[k] != null && String(initial[k]).trim() !== '') {
                base[k] = String(initial[k]).trim();
            }
        });
        return base;
    });
    const [companyPartnerSearch, setCompanyPartnerSearch] = useState(() => filters.company_name || '');
    const [filterPartners, setFilterPartners] = useState([]);
    const [filterPartnersLoading, setFilterPartnersLoading] = useState(false);
    const [filterPartnersError, setFilterPartnersError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = filterPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [filterPartners]);

    useEffect(() => {
        const loadFilterPartners = async () => {
            try {
                setFilterPartnersLoading(true);
                setFilterPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setFilterPartners(partnersArray);
            } catch (err) {
                setFilterPartners([]);
                setFilterPartnersError(handleApiError(err, 'Erreur lors du chargement des partenaires'));
            } finally {
                setFilterPartnersLoading(false);
            }
        };
        loadFilterPartners();
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleClear = () => {
        setFilters(emptyFilters());
        setCompanyPartnerSearch('');
        setError(null);
    };

    const handleExport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const companyName = (filters.company_name || companyPartnerSearch).trim();
            const query = buildQueryFromFilters({ ...filters, company_name: companyName });
            const data = await getAdminTripsCheckClaimsFullList(query);
            const rows = flattenTripsCheckClaimsForExcel(data);
            if (rows.length === 0) {
                setError('Aucun check claim à exporter pour ces filtres.');
                return;
            }
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'CheckClaims');
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
            XLSX.writeFile(workbook, `trips_check_claims_export_${stamp}.xlsx`);
        } catch (err) {
            setError(handleApiError(err, 'Erreur lors de l\'export Excel'));
        } finally {
            setLoading(false);
        }
    }, [filters, companyPartnerSearch]);

    return (
        <div className="dashboard-page admin-trips-claims-page">
            <Card>
                <Card.Body>
                    <div className="page-header mb-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                            <div>
                                <h2 className="page-title mb-1">
                                    <FiCheckCircle className="me-2" />
                                    Export Excel — Check Claims séjours
                                </h2>
                                <p className="text-muted mb-0">
                                    Données issues de <code>GET /api/trips/check-claims</code> (filtres optionnels), fichier{' '}
                                    <strong>.xlsx</strong> généré côté navigateur.
                                </p>
                            </div>
                            <Button
                                variant="outline-secondary"
                                className="d-flex align-items-center"
                                onClick={() => navigate('/admin/trips/check-claims')}
                            >
                                <FiArrowLeft className="me-2" />
                                Retour à la liste
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Card className="mb-4 filters-card" style={{ overflow: 'visible' }}>
                        <Card.Header>
                            <h6 className="mb-0">Filtres (optionnels)</h6>
                        </Card.Header>
                        <Card.Body style={{ overflow: 'visible' }}>
                            <Row className="g-3">
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control name="policy_code" value={filters.policy_code} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control name="reservation_number" value={filters.reservation_number} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Prénom client</Form.Label>
                                        <Form.Control name="client_first_name" value={filters.client_first_name} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Nom client</Form.Label>
                                        <Form.Control name="client_last_name" value={filters.client_last_name} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="client_email"
                                            value={filters.client_email}
                                            onChange={handleFilterChange}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Nom de société</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={filters.company_name}
                                        onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                        onInputChange={setCompanyPartnerSearch}
                                        options={partnerCompanyOptions}
                                        loading={filterPartnersLoading}
                                        error={filterPartnersError}
                                        placeholder="Rechercher un partenaire…"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Statut</Form.Label>
                                        <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                                            <option value="">Tous les statuts</option>
                                            <option value="Created">Created</option>
                                            <option value="Paid">Paid</option>
                                            <option value="CG confirmed">CG confirmed</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Jour du séjour</Form.Label>
                                        <Form.Control type="date" name="trip_day" value={filters.trip_day} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date début activité (de)</Form.Label>
                                        <Form.Control type="date" name="activity_start_date_from" value={filters.activity_start_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date début activité (à)</Form.Label>
                                        <Form.Control type="date" name="activity_start_date_to" value={filters.activity_start_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control type="date" name="activity_end_date_from" value={filters.activity_end_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control type="date" name="activity_end_date_to" value={filters.activity_end_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Dates séjour modifiées</Form.Label>
                                        <Form.Select name="trip_modified" value={filters.trip_modified} onChange={handleFilterChange}>
                                            <option value="">Tous</option>
                                            <option value="true">Oui</option>
                                            <option value="false">Non</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Nouveau début séjour (de)</Form.Label>
                                        <Form.Control type="date" name="new_trip_start_date_from" value={filters.new_trip_start_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Nouveau début séjour (à)</Form.Label>
                                        <Form.Control type="date" name="new_trip_start_date_to" value={filters.new_trip_start_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Nouvelle fin séjour (de)</Form.Label>
                                        <Form.Control type="date" name="new_trip_end_date_from" value={filters.new_trip_end_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Nouvelle fin séjour (à)</Form.Label>
                                        <Form.Control type="date" name="new_trip_end_date_to" value={filters.new_trip_end_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Début effectif (de)</Form.Label>
                                        <Form.Control type="date" name="new_trip_effective_start_date_from" value={filters.new_trip_effective_start_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Début effectif (à)</Form.Label>
                                        <Form.Control type="date" name="new_trip_effective_start_date_to" value={filters.new_trip_effective_start_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Fin effective (de)</Form.Label>
                                        <Form.Control type="date" name="new_trip_effective_end_date_from" value={filters.new_trip_effective_end_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Fin effective (à)</Form.Label>
                                        <Form.Control type="date" name="new_trip_effective_end_date_to" value={filters.new_trip_effective_end_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-flex flex-wrap gap-2 justify-content-end mt-3">
                                <Button variant="outline-secondary" onClick={handleClear} disabled={loading}>
                                    Réinitialiser les filtres
                                </Button>
                                <Button variant="success" className="d-flex align-items-center" onClick={handleExport} disabled={loading}>
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" animation="border" />
                                            Export en cours...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload className="me-2" />
                                            Télécharger Excel
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsCheckClaimsExport;
